import React, { memo } from 'react';
import { ReactPlayerProps } from 'react-player';
import { ENABLE_PLAYER_LOGGING } from 'config';

import { PlayerTypes } from 'models';
import DailyMotion from 'react-player/lib/players/DailyMotion';
import Facebook from 'react-player/lib/players/Facebook';
import Mixcloud from 'react-player/lib/players/Mixcloud';
import SoundCloud from 'react-player/lib/players/SoundCloud';
import Streamable from 'react-player/lib/players/Streamable';
import Twitch from 'react-player/lib/players/Twitch';
import Vimeo from 'react-player/lib/players/Vimeo';
import Wistia from 'react-player/lib/players/Wistia';
import YouTube from 'react-player/lib/players/YouTube';
import MaestroFilePlayer from './players/MaestroFilePlayer';

import { FaceMask } from './players/FaceMask';
import Iframe from './players/Iframe';
import { JWPlayer } from './players/JWPlayer';
import { PhenixPlayer } from './players/PhenixPlayer';
import { UstreamLive } from './players/UstreamLive';
import { UstreamVideo } from './players/UstreamVideo';
import { AmazonIVS } from './players/AmazonIVS';
import { useSelector } from 'react-redux';
import { getPageId, getSiteId } from 'services/app';
import { useVideoPlayerContext } from '../Player/Context';

export interface IReactPlayer extends ReactPlayerProps {
  controls: boolean;
  forwardRef?: React.RefObject<any>;
  isStreamActive?: boolean;
  playerType: PlayerTypes;
  playing: boolean;
}

export default memo(function ReactPlayer({
  isLivestreamPreview,
  controls,
  playing,
  playerType,
  ...playerProps
}: IReactPlayer) {
  const channelId = useSelector(getPageId);
  const siteId = useSelector(getSiteId);

  const { forwardRef, isStreamActive = false, ...restProps } = playerProps;

  const videoPlayerContext = useVideoPlayerContext();

  const props = {
    channelId,
    controls,
    isLivestreamPreview,
    playerType,
    playing,
    siteId,
    ...restProps,
    enableLogging: ENABLE_PLAYER_LOGGING,
  };

  const playerToRender: PlayerTypes = isLivestreamPreview
    ? restProps.playerType
    : playerType;

  const livestreamPlayerProps = {
    isLivestreamPreview,
    isStreamActive,
  };

  switch (playerToRender) {
    case PlayerTypes.amazon:
      return <AmazonIVS ref={forwardRef} {...props} />;
    case PlayerTypes.dailymotion:
      return <DailyMotion ref={forwardRef} {...props} />;
    case PlayerTypes.facemask:
      return <FaceMask ref={forwardRef} {...props} />;
    case PlayerTypes.iframe:
      return <Iframe ref={forwardRef} {...props} />;
    case PlayerTypes.facebook:
      return <Facebook ref={forwardRef} {...props} />;
    case PlayerTypes.jwplayer:
      return <JWPlayer ref={forwardRef} {...props} />;
    case PlayerTypes.mixcloud:
      return <Mixcloud ref={forwardRef} {...props} />;
    case PlayerTypes.phenix:
      return <PhenixPlayer ref={forwardRef} {...props} />;
    case PlayerTypes.soundcloud:
      return <SoundCloud ref={forwardRef} {...props} />;
    case PlayerTypes.streamable:
      return <Streamable ref={forwardRef} {...props} />;
    case PlayerTypes.youtube:
      return <YouTube ref={forwardRef} {...props} />;
    case PlayerTypes.ustreamLive:
      return <UstreamLive ref={forwardRef} {...props} />;
    case PlayerTypes.ustream:
      return <UstreamVideo ref={forwardRef} {...props} />;
    case PlayerTypes.vimeo:
      return <Vimeo ref={forwardRef} {...props} />;
    case PlayerTypes.wistia:
      return <Wistia ref={forwardRef} {...props} />;
    case PlayerTypes.twitch:
      return <Twitch ref={forwardRef} {...props} />;
    case PlayerTypes.livestream:
      return (
        <MaestroFilePlayer
          ref={forwardRef}
          {...props}
          {...livestreamPlayerProps}
        />
      );
    case PlayerTypes.file:
    default:
      return (
        <MaestroFilePlayer
          ref={forwardRef}
          {...props}
          selectedSubtitle={videoPlayerContext?.selectedSubtitle.value}
          videoPlayerContext={videoPlayerContext}
        />
      );
  }
});
